// The two clocks every timed mode needs, in one place.
//
// Both tick on an interval rather than requestAnimationFrame: these drive a
// number and a bar, not an animation, and rAF keeps running work queued on a
// backgrounded tab for no benefit. 100 ms is fine for a tenth-of-a-second
// readout and costs nothing.
//
// THEY STOP THEMSELVES. A countdown left running after its component unmounted
// is the classic version of this bug: he leaves Last Gasp mid-round, and
// ninety seconds later a timer that no longer has a screen calls its onEnd and
// pushes him to a results page from wherever he now is.

import { ref, onUnmounted } from 'vue';

const TICK = 100;

/** Counts DOWN from ms. Calls onEnd once, at zero. */
export function useCountdown(totalMs, onEnd) {
  const remaining = ref(totalMs);
  const running = ref(false);
  let handle = null;
  let endsAt = 0;
  let fired = false;

  function stop() {
    if (handle) clearInterval(handle);
    handle = null;
    running.value = false;
  }

  function start() {
    stop();
    fired = false;
    endsAt = Date.now() + remaining.value;
    running.value = true;
    handle = setInterval(() => {
      remaining.value = Math.max(0, endsAt - Date.now());
      if (remaining.value <= 0 && !fired) {
        fired = true;
        stop();
        onEnd?.();
      }
    }, TICK);
  }

  /** Wrong answers cost SECONDS, not lives — this is how. */
  function adjust(deltaMs) {
    endsAt += deltaMs;
    remaining.value = Math.max(0, endsAt - Date.now());
    if (remaining.value <= 0 && running.value && !fired) {
      fired = true;
      stop();
      onEnd?.();
    }
  }

  function reset(ms = totalMs) {
    stop();
    fired = false;
    remaining.value = ms;
  }

  onUnmounted(stop);
  return { remaining, running, start, stop, adjust, reset };
}

/** Counts UP. This is the Endurance score. */
export function useStopwatch() {
  const elapsed = ref(0);
  const running = ref(false);
  let handle = null;
  let startedAt = 0;

  function stop() {
    if (handle) clearInterval(handle);
    handle = null;
    running.value = false;
  }

  function start() {
    stop();
    startedAt = Date.now() - elapsed.value;
    running.value = true;
    handle = setInterval(() => {
      elapsed.value = Date.now() - startedAt;
    }, TICK);
  }

  function reset() {
    stop();
    elapsed.value = 0;
  }

  onUnmounted(stop);
  return { elapsed, running, start, stop, reset };
}
